import React, { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, ArrowRight, Bike, Store, Sparkles, Clock, Star, Loader2, Locate, BadgePlus, MapPin } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import CategoryPills from "@/components/CategoryPills";
import RestaurantCard from "@/components/RestaurantCard";
import InstallQR from "@/components/InstallQR";
import { imgUrl } from "@/utils/format";
import { roleHome } from "@/utils/roles";
import { useAuth } from "@/contexts/AuthContext";
import { useDeliveryAddress } from "@/contexts/AddressContext";
import { isNativeApp } from "@/utils/platform";
import { nearestZone } from "@/utils/conakryZones";

const GRID_IMGS = [
  imgUrl("/api/static/images/dish_poulet_brais.png"),
  imgUrl("/api/static/images/dish_atti_k_poisson.png"),
  imgUrl("/api/static/images/dish_brochettes_de_b_uf.png"),
  imgUrl("/api/static/images/dish_foufou_sauce_gombo.png"),
  imgUrl("/api/static/images/dish_jus_de_bissap.png"),
  imgUrl("/api/static/images/dish_maf_b_uf.png"),
];

/** Horizontal scrollable row of restaurant cards, Deliveroo-style. */
function ScrollRow({ kicker, kickerColor, icon, title, seeAllTo, seeAllTestId, restaurants, testId }) {
  const { t } = useTranslation();
  if (!restaurants.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5" data-testid={testId}>
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className={`text-xs uppercase tracking-[0.25em] ${kickerColor} flex items-center gap-1.5`}>{icon}{kicker}</div>
          <h2 className="font-display text-xl sm:text-2xl font-semibold">{title}</h2>
        </div>
        <Link to={seeAllTo} data-testid={seeAllTestId} className="text-sm text-brand-red font-semibold inline-flex items-center gap-1 shrink-0">
          {t("home.seeAll")} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3 -mx-4 px-4 snap-x snap-mandatory" style={{ scrollbarWidth: "thin" }}>
        {restaurants.map((r) => (
          <div key={r.restaurant_id} className="w-72 sm:w-80 shrink-0 snap-start">
            <RestaurantCard restaurant={r} />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { selected: rawDeliveryAddr, select: selectAddr } = useDeliveryAddress();
  // Le filtrage par adresse ne s'applique que dans l'app mobile (Capacitor)
  const deliveryAddr = isNativeApp ? rawDeliveryAddr : null;
  const [restaurants, setRestaurants] = useState([]);
  const [cats, setCats] = useState([]);
  const [homeCat, setHomeCat] = useState(null); // instant category filter (no navigation)
  const [myOrders, setMyOrders] = useState([]); // historique client → personnalisation des rangées
  const [search, setSearch] = useState("");
  const [detecting, setDetecting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    apiClient.get("/restaurants").then(({ data }) => setRestaurants(data)).catch(() => {});
    apiClient.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, [i18n.language]);

  useEffect(() => {
    if (user?.role === "client") {
      apiClient.get("/orders/me").then(({ data }) => setMyOrders(data.items || [])).catch(() => {});
    } else {
      setMyOrders([]);
    }
  }, [user?.user_id]);

  // Partners land straight on their work screen — zero navigation to start working
  if (user && user.role !== "client") {
    return <Navigate to={roleHome(user.role)} replace />;
  }

  const catLabel = (key) => cats.find((c) => c.key === key)?.label || key;

  // Filtre par zone de livraison de l'adresse sélectionnée dans le header
  const zoneFiltered = deliveryAddr?.zone
    ? restaurants.filter((r) => r.zone === deliveryAddr.zone)
    : restaurants;

  const filtered = homeCat
    ? zoneFiltered.filter((r) => (r.categories || []).includes(homeCat))
    : zoneFiltered;
  const popular = [...zoneFiltered].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 8);
  const quick = [...zoneFiltered].sort((a, b) => (a.delivery_time_min || 99) - (b.delivery_time_min || 99)).slice(0, 8);
  const newest = [...zoneFiltered].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))).slice(0, 8);

  // Rangées personnalisées : catégories les plus commandées par le client en premier
  // (tri stable → à égalité, l'ordre par défaut des catégories est conservé)
  const catScore = {};
  myOrders.forEach((o) => {
    const resto = restaurants.find((r) => r.restaurant_id === o.restaurant_id);
    (resto?.categories || []).forEach((k) => { catScore[k] = (catScore[k] || 0) + 1; });
  });
  const sortedCats = [...cats].sort((a, b) => (catScore[b.key] || 0) - (catScore[a.key] || 0));

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/restaurants?q=${encodeURIComponent(search.trim())}`);
    else navigate("/restaurants");
  };

  const isInIframe = typeof window !== "undefined" && window.self !== window.top;

  const handleDetectLocation = () => {
    if (detecting) return;
    if (!("geolocation" in navigator)) {
      toast.error(t("home.detectUnsupported"));
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDetecting(false);
        const match = nearestZone(pos.coords.latitude, pos.coords.longitude);
        if (match) {
          toast.success(t("home.detectSuccess", { zone: match.zone }));
          navigate(`/restaurants?zone=${encodeURIComponent(match.zone)}`);
        } else {
          toast.info(t("home.detectFallback"));
          navigate("/restaurants");
        }
      },
      (err) => {
        setDetecting(false);
        if (err.code === err.PERMISSION_DENIED) {
          toast.error(t("home.detectDenied"));
          if (isInIframe) toast(t("home.detectIframeHint"), { duration: 8000 });
        } else {
          toast.error(t("home.detectError"));
        }
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  return (
    <div>
      {/* COMPACT HERO + PROMINENT SEARCH */}
      <section className="bg-white border-b border-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-5">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-brand-red mb-2">
            <Sparkles className="w-3 h-3" /> {t("home.tagline")}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-ink leading-[1.1] max-w-2xl">
            {t("home.title")}
          </h1>
          <form onSubmit={handleSearch} className="mt-5 bg-elevated rounded-2xl border border-sand p-2 flex flex-col sm:flex-row gap-2 max-w-3xl">
            <div className="flex items-center gap-2 px-3 flex-1">
              <button
                type="button"
                onClick={handleDetectLocation}
                disabled={detecting}
                data-testid="hero-detect-location"
                aria-label={t("home.detectLocation")}
                title={t("home.detectLocation")}
                className="shrink-0 rounded-full p-1.5 hover:bg-white text-mute hover:text-brand-red disabled:opacity-60 transition-colors"
              >
                {detecting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Locate className="w-5 h-5" />}
              </button>
              <input
                type="text"
                data-testid="hero-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("restaurants.searchPlaceholder")}
                className="bg-transparent outline-none w-full py-3 text-sm"
              />
            </div>
            <button data-testid="hero-search-btn" className="rounded-xl bg-brand-red text-white px-6 py-3 font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#C81F2D]">
              <Search className="w-4 h-4" /> {t("home.heroSearchCta")}
            </button>
          </form>

          {/* ACQUISITION — devenir restaurant partenaire / livreur */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="home-acquisition-cards">
            <div className="rounded-2xl bg-white border border-sand p-3.5 flex items-center gap-3 shadow-sm" data-testid="home-acq-resto">
              <div className="w-11 h-11 rounded-xl bg-brand-red/10 text-brand-red grid place-items-center shrink-0">
                <Store className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-sm text-ink">{t("home.acqRestoTitle")}</div>
                <p className="text-xs text-mute mt-0.5 line-clamp-2">{t("home.acqRestoText")}</p>
              </div>
              <Link
                to="/devenir-partenaire"
                data-testid="home-acq-resto-cta"
                className="shrink-0 rounded-full bg-brand-red text-white px-3.5 py-2 text-xs font-semibold hover:bg-[#C81F2D] transition-colors whitespace-nowrap"
              >
                {t("home.acqRestoCta")}
              </Link>
            </div>
            <div className="rounded-2xl bg-white border border-sand p-3.5 flex items-center gap-3 shadow-sm" data-testid="home-acq-driver">
              <div className="w-11 h-11 rounded-xl bg-brand-red/10 text-brand-red grid place-items-center shrink-0">
                <Bike className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-sm text-ink">{t("home.acqDriverTitle")}</div>
                <p className="text-xs text-mute mt-0.5 line-clamp-2">{t("home.acqDriverText")}</p>
              </div>
              <Link
                to="/devenir-livreur"
                data-testid="home-acq-driver-cta"
                className="shrink-0 rounded-full bg-brand-red text-white px-3.5 py-2 text-xs font-semibold hover:bg-[#C81F2D] transition-colors whitespace-nowrap"
              >
                {t("home.acqDriverCta")}
              </Link>
            </div>
          </div>

          {/* CATEGORY BAND — instant filter, no navigation */}
          <div className="mt-4" data-testid="home-categories">
            <CategoryPills value={homeCat} onChange={(c) => setHomeCat(c)} />
          </div>

          {/* Indicateur de zone de livraison sélectionnée */}
          {deliveryAddr?.zone && (
            <div className="mt-3 flex items-center flex-wrap gap-2 text-xs text-mute" data-testid="home-zone-banner">
              <MapPin className="w-3.5 h-3.5 text-brand-red" />
              <span data-testid="home-zone-count">{t("addr.zoneCount", { count: zoneFiltered.length, zone: deliveryAddr.zone })}</span>
              <button onClick={() => selectAddr(null)} data-testid="home-zone-clear" className="text-brand-red font-semibold underline underline-offset-2">
                {t("addr.showAll")}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Aucun restaurant dans la zone sélectionnée */}
      {deliveryAddr?.zone && zoneFiltered.length === 0 ? (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="home-zone-empty">
          <div className="bg-white border border-sand rounded-2xl p-10 text-center">
            <MapPin className="w-8 h-8 text-brand-red mx-auto mb-3" />
            <div className="font-display text-lg font-semibold">{t("addr.zoneEmptyTitle", { zone: deliveryAddr.zone })}</div>
            <p className="text-sm text-mute mt-1 max-w-md mx-auto">{t("addr.zoneEmptyText")}</p>
            <button onClick={() => selectAddr(null)} data-testid="home-zone-empty-show-all" className="mt-4 rounded-full bg-brand-red text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#C81F2D]">
              {t("addr.showAll")}
            </button>
          </div>
        </section>
      ) : homeCat ? (
        /* INSTANT FILTERED LIST */
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="home-filtered-section">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="font-display text-xl sm:text-2xl font-semibold">{catLabel(homeCat)}</h2>
              <div className="text-xs text-mute mt-0.5" data-testid="home-filtered-count">
                {filtered.length} restaurant{filtered.length > 1 ? "s" : ""}
              </div>
            </div>
            <button
              onClick={() => navigate(`/restaurants?category=${homeCat}`)}
              data-testid="home-see-all-category"
              className="rounded-full bg-brand-red text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#C81F2D] inline-flex items-center gap-1.5"
            >
              Tout voir dans Restaurants <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {filtered.length === 0 ? (
            <div className="bg-white border border-sand rounded-2xl p-10 text-center" data-testid="home-filtered-empty">
              <div className="text-3xl mb-2">🔍</div>
              <div className="font-display text-lg font-semibold">Aucun restaurant {catLabel(homeCat)} pour le moment</div>
              <button onClick={() => setHomeCat(null)} className="mt-4 rounded-full border border-sand hover:border-brand-red px-5 py-2 text-sm font-semibold">
                Voir toutes les cuisines
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="home-filtered-grid">
              {filtered.map((r) => <RestaurantCard key={r.restaurant_id} restaurant={r} />)}
            </div>
          )}
        </section>
      ) : (
        /* DELIVEROO-STYLE HORIZONTAL SECTIONS */
        <div className="pt-3">
          <ScrollRow
            kicker={t("home.favorites")}
            kickerColor="text-brand-red"
            icon={<Star className="w-3 h-3" />}
            title={t("home.popularNear")}
            seeAllTo="/restaurants?sort=rating"
            seeAllTestId="see-all-popular"
            restaurants={popular}
            testId="home-row-popular"
          />
          <ScrollRow
            kicker={t("home.deliveryTitle")}
            kickerColor="text-brand-green"
            icon={<Clock className="w-3 h-3" />}
            title={t("home.fastDelivery")}
            seeAllTo="/restaurants?max_delivery_time=25"
            seeAllTestId="see-all-quick"
            restaurants={quick}
            testId="home-row-quick"
          />
          <ScrollRow
            kicker={t("home.newLabel")}
            kickerColor="text-[#B8860B]"
            icon={<BadgePlus className="w-3 h-3" />}
            title={t("home.newOnNeldaari")}
            seeAllTo="/restaurants"
            seeAllTestId="see-all-new"
            restaurants={newest}
            testId="home-row-new"
          />
          {/* Rangées par catégorie de découverte, les plus commandées d'abord */}
          {sortedCats.map((c) => {
            const inCat = zoneFiltered.filter((r) => (r.categories || []).includes(c.key));
            if (inCat.length === 0) return null;
            return (
              <ScrollRow
                key={c.key}
                kicker={t("home.discoverLabel")}
                kickerColor="text-brand-red"
                icon={<span className="text-[10px] leading-none">{c.emoji}</span>}
                title={c.label}
                seeAllTo={`/restaurants?category=${c.key}`}
                seeAllTestId={`see-all-cat-${c.key}`}
                restaurants={inCat}
                testId={`home-row-cat-${c.key}`}
              />
            );
          })}
        </div>
      )}

      {/* COMMANDEZ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-elevated rounded-3xl p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-brand-red mb-2">{t("home.orderKicker")}</div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold mb-3">{t("home.orderTitle")}</h2>
            <p className="text-mute mb-5 max-w-lg">
              {t("home.orderText")}
            </p>
            <Link to="/restaurants" data-testid="cta-explore" className="inline-flex items-center gap-2 bg-brand-red hover:bg-[#C81F2D] text-white px-6 py-3 rounded-full font-semibold">
              {t("home.orderCta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {GRID_IMGS.map((src, i) => (
              <img key={i} src={src} alt="" className={`rounded-2xl aspect-square object-cover ${i % 2 === 1 ? "translate-y-6" : ""}`} />
            ))}
          </div>
        </div>
      </section>

      {/* INSTALL QR (desktop) / share (mobile) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <InstallQR />
      </section>
    </div>
  );
}
